import _ from 'underscore';

import {assert} from 'Asserts';

import {getCDRAnnotations, getNumbering} from './AntibodyEngineeringActions';
import {
  CDRAnnotations,
  CDRDefinition,
  CDRResponse,
  ChainType,
  InvalidCDRResult,
  InvalidNumberingResult,
  Numbering,
  NumberingResponse,
  NumberingScheme,
  Organism,
  ValidCDRResult,
  ValidNumberingResult,
} from './AntibodyEngineeringConstants';

enum FetchType {
  NUMBERING = 'NUMBERING',
  CDR = 'CDR',
}
export interface MatchMetadata {
  organism: Organism;
  chainType: ChainType;
}
export interface StoreData {
  metadata: {
    [aminoAcids: string]: Array<MatchMetadata>;
  };
  validNumbering: {
    [aminoAcids: string]: {
      [scheme in NumberingScheme]: Array<ValidNumberingResult> | null;
    };
  };
  invalidNumbering: {
    [aminoAcids: string]: {
      [scheme in NumberingScheme]: Array<InvalidNumberingResult> | null;
    };
  };
  validCdrAnnotations: {
    [aminoAcids: string]: {
      [definition in CDRDefinition]: Array<ValidCDRResult> | null;
    };
  };
  invalidCdrAnnotations: {
    [aminoAcids: string]: {
      [definition in CDRDefinition]: Array<InvalidCDRResult> | null;
    };
  };
  promises: {
    [k: string]: Promise<void>;
  };
}

/**
 * Class to store antibody analysis per protein sequence. Numbering and CDR annotations are dependent
 * only on a protein's residues, so we key by residues (not by protein id). The store will cache and merge
 * responses so the client should only have to fetch each residues/numbering or residues/definition once.
 */
export class _AntibodyAnalysisStore {
  readonly data: StoreData;

  constructor(seedData?: StoreData) {
    if (seedData && !global.__NODE_TESTING__) {
      assert(
        false,
        'Seeding is only for tests, use instantiated exported stores instead of building your own'
      );
    }
    this.data = seedData || {
      metadata: {},
      validNumbering: {},
      invalidNumbering: {},
      validCdrAnnotations: {},
      invalidCdrAnnotations: {},
      promises: {},
    };
  }

  private getCachedAnnotations(residues: string, definition: CDRDefinition): Array<CDRAnnotations> | null {
    return this.data.validCdrAnnotations[residues]?.[definition] || null;
  }

  private getCachedNumbering(residues: string, scheme: NumberingScheme): Array<Numbering> | null {
    return this.data.validNumbering[residues]?.[scheme] || null;
  }

  private numberingHasFetched(residues: string, scheme: NumberingScheme): boolean {
    return (
      Boolean(this.data.validNumbering[residues]?.[scheme]) ||
      Boolean(this.data.invalidNumbering[residues]?.[scheme])
    );
  }

  private annotationsHaveFetched(residues: string, definition: CDRDefinition): boolean {
    return (
      Boolean(this.data.validCdrAnnotations[residues]?.[definition]) ||
      Boolean(this.data.invalidCdrAnnotations[residues]?.[definition])
    );
  }

  private initializeNumberingDataForResidues(root: {[k: string]: unknown}, residues: string): void {
    if (!root[residues]) {
      root[residues] = {
        [NumberingScheme.IMGT]: null,
        [NumberingScheme.CHOTHIA]: null,
        [NumberingScheme.KABAT]: null,
      };
    }
  }

  private initializeCdrDataForResidues(root: {[k: string]: unknown}, residues: string): void {
    if (!root[residues]) {
      root[residues] = {
        [CDRDefinition.KABAT]: null,
        [CDRDefinition.IMGT]: null,
        [CDRDefinition.CHOTHIA]: null,
        [CDRDefinition.NORTH]: null,
      };
    }
  }

  private addNumberingToStore(response: NumberingResponse): void {
    const validResults = response.results.filter((r) => !r.message) as Array<ValidNumberingResult>;
    const invalidResults = response.results.filter((r) => r.message) as Array<InvalidNumberingResult>;
    if (validResults.length > 0) {
      this.initializeNumberingDataForResidues(this.data.validNumbering, response.residues);
      this.data.validNumbering[response.residues][response.scheme] = validResults;
    }
    if (invalidResults.length > 0) {
      this.initializeNumberingDataForResidues(this.data.invalidNumbering, response.residues);
      this.data.invalidNumbering[response.residues][response.scheme] = invalidResults;
    }
  }

  private addAnnotationsToStore(response: CDRResponse): void {
    const validResults = response.results.filter((r) => !r.message) as Array<ValidCDRResult>;
    const invalidResults = response.results.filter((r) => r.message) as Array<InvalidCDRResult>;
    if (validResults.length > 0) {
      this.initializeCdrDataForResidues(this.data.validCdrAnnotations, response.residues);
      this.data.validCdrAnnotations[response.residues][response.definition] = validResults;
    }
    if (invalidResults.length > 0) {
      this.initializeCdrDataForResidues(this.data.invalidCdrAnnotations, response.residues);
      this.data.invalidCdrAnnotations[response.residues][response.definition] = invalidResults;
    }
  }

  private addMetadataToStore(response: NumberingResponse | CDRResponse): void {
    this.data.metadata[response.residues] = (response.results as Array<MatchMetadata>).map(
      ({organism, chainType}: MatchMetadata) => ({
        organism,
        chainType,
      })
    );
  }

  private makePromiseKey(
    residues: string,
    fetchType: FetchType,
    dataType: NumberingScheme | CDRDefinition
  ): string {
    return `${residues}-${fetchType}-${dataType}`;
  }

  /**
   * Waits for all requests specified by the given promiseKeys to be completed.
   * If all keys have promises already in the cache, waits for those existing promises to resolve.
   * Otherwise calls the fetchFn and waits for it to resolve.
   * (NB: Callers should be able to await this function assuming that when it resolves any data fetched will
   * be present in the store. Thus the fetchFn should not just make a network request but also handle adding
   * the result (data or errors) to the store.)
   */
  private async getOrMakeRequest(fetchFn: () => Promise<void>, promiseKeys: Array<string>): Promise<void> {
    const existingPromises = promiseKeys.map((key) => this.data.promises[key]);

    if (existingPromises.every((p) => Boolean(p))) {
      await Promise.all(existingPromises);
      return;
    }

    const newPromise = fetchFn();
    promiseKeys.forEach((key) => {
      this.data.promises[key] = newPromise;
    });
    await newPromise;
  }

  private async fetchCDRAnnotationsForStore(
    aaSequences: Array<string>,
    definition: CDRDefinition
  ): Promise<void> {
    const promiseKeys = aaSequences.map((residues) =>
      this.makePromiseKey(residues, FetchType.CDR, definition)
    );
    const fetchFn = async (): Promise<void> => {
      const response = await getCDRAnnotations(aaSequences, definition);
      for (const aaResponse of response) {
        this.addAnnotationsToStore(aaResponse);
        this.addMetadataToStore(aaResponse);
      }
    };
    await this.getOrMakeRequest(fetchFn, promiseKeys);
  }

  private async fetchNumberingsForStore(aaSequences: Array<string>, scheme: NumberingScheme): Promise<void> {
    const promiseKeys = aaSequences.map((residues) =>
      this.makePromiseKey(residues, FetchType.NUMBERING, scheme)
    );
    const fetchFn = async (): Promise<void> => {
      const response = await getNumbering(aaSequences, scheme);
      for (const aaResponse of response) {
        this.addNumberingToStore(aaResponse);
        this.addMetadataToStore(aaResponse);
      }
    };
    await this.getOrMakeRequest(fetchFn, promiseKeys);
  }

  /**
   * Get either numbering or CDR annotations for the requested amino acids from the store, fetching if not
   * already present. If the server returns an error for any of the amino acids, return null.
   */
  private async getData<T, V>(
    aaSequences: Array<string>,
    type: T,
    getMethod: (residues: string, type: T) => V | null,
    hasFetched: (residues: string, type: T) => boolean,
    fetchMethod: (aaSequences: Array<string>, type: T) => Promise<void>
  ): Promise<Array<V | null>> {
    const capitalizedAcids = aaSequences.map((residues) => residues.toUpperCase());
    if (capitalizedAcids.every((residues) => hasFetched(residues, type))) {
      return capitalizedAcids.map((residues) => getMethod(residues, type));
    }

    const toFetch = capitalizedAcids.filter((residues) => !hasFetched(residues, type));
    await fetchMethod(toFetch, type);

    return capitalizedAcids.map((residues) => getMethod(residues, type));
  }

  /**
   * Get CDR annotations for the requested amino acids from the store, fetching if not already present.
   * It is the caller's responsibility to ask the store about errors separately.
   */
  public async getCDRAnnotations(
    aaSequences: Array<string>,
    definition: CDRDefinition
  ): Promise<Array<Array<ValidCDRResult> | null>> {
    return await this.getData<CDRDefinition, Array<ValidCDRResult>>(
      aaSequences,
      definition,
      this.getCachedAnnotations.bind(this),
      this.annotationsHaveFetched.bind(this),
      this.fetchCDRAnnotationsForStore.bind(this)
    );
  }

  /**
   * Get numbering for the requested amino acids from the store, fetching if not already present.
   * It is the caller's responsibility to ask the store about errors separately.
   */
  public async getNumbering(
    aaSequences: Array<string>,
    scheme: NumberingScheme
  ): Promise<Array<Array<ValidNumberingResult> | null>> {
    return await this.getData<NumberingScheme, Array<ValidNumberingResult>>(
      aaSequences,
      scheme,
      this.getCachedNumbering.bind(this),
      this.numberingHasFetched.bind(this),
      this.fetchNumberingsForStore.bind(this)
    );
  }

  /**
   * Returns any error message present in the store for the given AAs and numbering scheme.
   * Does not trigger a fetch if the AAs are not in the store.
   */
  public getCachedNumberingMessage(residues: string, scheme: NumberingScheme): string | undefined {
    const capitalized = residues.toUpperCase();
    const messages = this.data.invalidNumbering[capitalized]?.[scheme]?.map((r) => r.message);
    if (!messages) {
      return undefined;
    }

    // eslint-disable-next-line no-restricted-syntax
    const uniqMessages = _.uniq(messages);
    if (uniqMessages.length === 1) {
      return uniqMessages[0];
    }
    return uniqMessages.join('; ');
  }

  /**
   * Returns any error message present in the store for the given AAs and CDR definition.
   * Does not trigger a fetch if the AAs are not in the store.
   */
  public getCachedCDRMessage(residues: string, definition: CDRDefinition): string | undefined {
    const capitalized = residues.toUpperCase();
    const messages = this.data.invalidCdrAnnotations[capitalized]?.[definition]?.map((r) => r.message);
    if (!messages) {
      return undefined;
    }

    // eslint-disable-next-line no-restricted-syntax
    const uniqMessages = _.uniq(messages);
    if (uniqMessages.length === 1) {
      return uniqMessages[0];
    }
    return uniqMessages.join('; ');
  }

  /**
   * Returns the metadata (organism and chain type) for the given AAs if it is present in the store.
   * Does not trigger a fetch if the AAs are not in the store.
   */
  public getCachedMetadata(residues: string): Array<MatchMetadata> | undefined {
    const capitalized = residues.toUpperCase();
    return this.data.metadata[capitalized];
  }
}

const antibodyAnalysisStore = new _AntibodyAnalysisStore();

export default antibodyAnalysisStore;
